import {
    Box, Button, Grid, TextField, Typography, IconButton, MenuItem, Select
} from "@mui/material";
import CustomContainer from "../../components/CustomContainer";
import PageHeader from "../../components/PageHeader";
import AppCard from "../../components/ui/AppCard";
import { FaPen, FaPlus, FaTrash } from "react-icons/fa6";
import { useQuery } from "@tanstack/react-query";
import { Autocomplete, FormControl, InputLabel } from "@mui/material";
import { createQuote } from "../../helpers/httpRequests/quotes";
import appToast from "../../components/appToast";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useModal } from "../../contexts/ModalProvider";
import { createService, getServices } from "../../helpers/httpRequests/services";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers";
import { isOutsideDeliveryHours, isWeekend, moneyFormat, nextBusinessDay } from "../../utils/functions";
import { metalList } from "../../utils/variables";

const formatOptions = (data) => data?.data?.data?.map(item => ({ value: parseInt(item.id), label: item.name, price: item?.price })) ?? [];

const styles = {
    formContainer: {
        my: '2rem'
    },
    headerBox: {
        mt: '2rem',
        display: 'flex',
        justifyContent: 'space-between'
    },
    card: {
        p: '1.5rem'
    },
    submitButtonContainer: {
        display: "flex",
        justifyContent: "end",
        mt: "1.5rem"
    },
    imageGridContainer: {
        p: 2,
        justifyContent: 'left',
        mb: 3
    },
    imageGridItem: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end'
    },
    deleteButton: {
        position: 'absolute',
        top: 4,
        right: 4
    },
    imagePreview: {
        borderRadius: 1,
        width: '100%',
        cursor: 'pointer'
    },
    addImageButtonContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
    }
};

const minDate = nextBusinessDay(dayjs(), 2);
const DELIVERY_MIN = dayjs().hour(8).minute(30).second(0);
const DELIVERY_MAX = dayjs().hour(17).minute(0).second(0);

export default function ServiceNew() {

    const navigate = useNavigate();

    const { setImage, handleOpen } = useModal();

    const [images, setImages] = useState([]);
    const [service, setService] = useState(0);
    const [currentPrice, setCurrentPrice] = useState(0);
    const [selectedMetal, setSelectedMetal] = useState(1);
    const [deliveryDate, setDeliveryDate] = useState(minDate);

    const servicesQuery = useQuery({
        queryKey: ['getServices'],
        queryFn: getServices,
        staleTime: 1000 * 60
    });

    const services = formatOptions(servicesQuery.data);

    const handleMultipleImageUpload = (e) => {
        if (images.length >= 3) return;

        const file = e.target.files[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const size = 500;
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');
                    const aspectRatio = img.width / img.height;
                    let drawWidth, drawHeight, offsetX, offsetY;

                    if (aspectRatio > 1) {
                        drawWidth = size * aspectRatio;
                        drawHeight = size;
                        offsetX = -(drawWidth - size) / 2;
                        offsetY = 0;
                    } else {
                        drawWidth = size;
                        drawHeight = size / aspectRatio;
                        offsetX = 0;
                        offsetY = -(drawHeight - size) / 2;
                    }

                    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                    const base64Image = canvas.toDataURL('image/jpeg');

                    setImages((prevImages) => [...prevImages, base64Image]);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (index) => {
        setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    };

    const handleCreateOrder = async (e) => {
        e.preventDefault();

        if (images.length < 1) {

            appToast.error('Para solicitar um orçamento, você deve subir ao menos uma imagem.');
            return;
        };

        if (service == 0) {

            appToast.error('Para solicitar um orçamento, você deve escolher um serviço.');
            return;
        };

        if (selectedMetal == 0) {

            appToast.error('Para solicitar um orçamento, você deve escolher um material.');
            return;
        };

        if (!deliveryDate || !deliveryDate.isValid() || isWeekend(deliveryDate) || isOutsideDeliveryHours(deliveryDate)) {
            appToast.error('A entrega é de segunda a sexta, das 8h30 às 17h.');
            return;
        }

        const formData = new FormData(e.currentTarget);

        const payload = {
            service: service,
            selectedMetal: formData.get('selectedMetal'),
            description: formData.get('description'),
            delivery_date: deliveryDate.format('DD/MM/YYYY HH:mm'),
            images: images
        };

        try {
            await createService(payload);
            appToast.success("Novo Serviço cadastrado com sucesso!");
            navigate('/pedidos');
        } catch (error) {
            appToast.error(error.message || "Erro ao criar serviço.");
        }
    };

    const handleOpenFullScreenImage = (event) => {
        setImage(event.target.currentSrc);
        handleOpen();
    };

    useEffect(() => {

        if (service != 0) {
            const currentService = services.filter(obj => {
                return obj.value == service
            });

            const price = currentService[0]?.price || 0;

            setCurrentPrice(moneyFormat(price))
        } else {
            setCurrentPrice(0)
        };

    }, [service]);

    return (
        <CustomContainer>
            <Box component={'form'} onSubmit={handleCreateOrder} sx={styles.formContainer}>
                <PageHeader title="Solicitar serviço" crumbs={["Solicitar serviço"]} />

                <AppCard sx={styles.imageGridContainer}>
                <Grid
                    gap={2}
                    container
                    size={{ xs: 12 }}
                    display="flex"
                    justifyContent="center"
                >
                    {images.map((src, index) => (
                        <Grid
                            key={index}
                            size={{ xs: 6, lg: 1.5 }}
                            sx={styles.imageGridItem}
                        >
                            <IconButton
                                size="small"
                                variant="contained"
                                color="error"
                                onClick={() => handleRemoveImage(index)}
                                sx={styles.deleteButton}
                            >
                                <FaTrash />
                            </IconButton>
                            <Box
                                component={'img'}
                                sx={styles.imagePreview}
                                src={src}
                                onClick={handleOpenFullScreenImage}
                                alt={`Preview ${index + 1}`}
                            />
                        </Grid>
                    ))}
                    {(images.length < 3) && (
                        <Grid
                            sx={styles.addImageButtonContainer}
                            size={{ xs: 6, lg: 1.5 }}
                        >
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<FaPen size={16} />}
                            >
                                Incluir imagem
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleMultipleImageUpload}
                                />
                            </Button>
                        </Grid>
                    )}
                </Grid>
                </AppCard>

                <AppCard>
                    <Grid container spacing={3}>
                        <Grid size={12}>
                            <Typography variant="h4" textAlign="left">Informações da Joia</Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                options={services}
                                getOptionLabel={opt => opt.label}
                                value={services.find(opt => opt.value === service) || null}
                                onChange={(_, newValue) => {
                                    setService(newValue ? newValue.value : 0);
                                    setCurrentPrice(newValue ? newValue.price : 0);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Serviço"
                                        required
                                        fullWidth
                                        name="service"
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl required fullWidth >
                                <InputLabel>Material</InputLabel>
                                <Select
                                    fullWidth
                                    name="selectedMetal"
                                    label="Material"
                                    value={selectedMetal}
                                    onChange={(e) => setSelectedMetal(Number(e.target.value))}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                >
                                    {metalList?.map((opt, index) => (
                                        <MenuItem key={index} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                multiline
                                required
                                rows={7}
                                label="Descrição para Produção"
                                fullWidth
                                name="description"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <DateTimePicker
                                value={deliveryDate}
                                onChange={setDeliveryDate}
                                shouldDisableDate={isWeekend}
                                minDate={minDate}
                                minTime={DELIVERY_MIN}
                                maxTime={DELIVERY_MAX}
                                minutesStep={15}
                                ampm={false}
                                label="Data e hora de entrega"
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        helperText: 'Segunda a sexta, das 8h30 às 17h. Mínimo de dois dias úteis.',
                                    },
                                }}
                            />
                        </Grid>
                        {
                            currentPrice != 0 && (
                                <Grid size={12} textAlign={'right'}>
                                    <Typography variant="h5">Valor do serviço: <strong>{currentPrice}</strong></Typography>
                                    <Typography variant="caption" color="grey">Este pedido será confirmado assim que recebido</Typography>
                                </Grid>
                            )
                        }
                    </Grid>
                </AppCard>

                <Box sx={styles.submitButtonContainer}>
                    <Button
                        startIcon={<FaPlus />}
                        variant="contained"
                        color="secondary"
                        type="submit"
                    >
                        Cadastrar serviço
                    </Button>
                </Box>
            </Box>
        </CustomContainer>
    );
};